import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserResponse {
  id: string;
  email: string;
  full_name: string;
  picture_url: string | null;
  roles: string[];
  is_active: boolean;
  is_superadmin: boolean;
  // Whether the *current session* (the JWT) is acting as superadmin. Set to
  // false when a superadmin has switched into a lower role even though
  // is_superadmin (DB identity) is still true.
  currently_superadmin: boolean;
  employee_id?: string | null;
  active_role: string | null;
  // Roles the current JWT actually carries — drives UI gating since backend
  // enforcement follows the JWT, not the DB role list.
  effective_roles: string[];
}

interface AuthState {
  user: UserResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Permissions decoded from the current access token. Empty when logged out. */
  permissions: string[];
}

interface AuthContextValue extends AuthState {
  login: (redirectUri?: string) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  setSession: (access: string, refresh: string, expiresIn: number) => Promise<void>;
  switchRole: (targetRole: string | null) => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  /** Flat list of permission strings decoded from the JWT access token. */
  permissions: string[];
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

const ACCESS_KEY = "hrms_access_token";
const REFRESH_KEY = "hrms_refresh_token";
const EXPIRES_KEY = "hrms_token_expires";

// ---------------------------------------------------------------------------
// Feature flag: cookie-based refresh (issue #80 migration)
// ---------------------------------------------------------------------------
// When enabled, the refresh flow calls POST /auth/refresh-cookie (which reads
// the refresh token from an httpOnly cookie set by the backend on login) and
// stops reading hrms_refresh_token from localStorage.
//
// Default: OFF. Will be flipped to ON on staging via VITE_USE_COOKIE_REFRESH=1
// once /auth/refresh-cookie is verified end-to-end. See
// docs/security/refresh-cookie-migration.md for the full rollout plan.
// Default: ON. Set VITE_USE_COOKIE_REFRESH=0 at build time to disable (rollback path).
const USE_COOKIE_REFRESH: boolean =
  import.meta.env.VITE_USE_COOKIE_REFRESH !== "0" &&
  import.meta.env.VITE_USE_COOKIE_REFRESH !== "false";

function storeTokens(access: string, refresh: string, expiresIn: number): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
  localStorage.setItem(
    EXPIRES_KEY,
    String(Date.now() + expiresIn * 1000)
  );
}

function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

function getTokenExpiry(): number {
  return parseInt(localStorage.getItem(EXPIRES_KEY) ?? "0", 10);
}

// ---------------------------------------------------------------------------
// JWT payload decode (no signature verification — backend remains authoritative)
// ---------------------------------------------------------------------------

interface JwtPayload {
  permissions?: string[];
  roles?: string[];
  [key: string]: unknown;
}

/**
 * Decode the payload of a JWT without verifying its signature.
 * Used purely for client-side UI gating — backend permission checks are
 * the source of truth on every API call.
 */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    const payloadPart = parts[1];
    if (parts.length !== 3 || !payloadPart) return null;
    // base64url -> base64
    const b64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function permissionsFromToken(token: string | null): string[] {
  if (!token) return [];
  const payload = decodeJwtPayload(token);
  if (!payload || !Array.isArray(payload.permissions)) return [];
  return payload.permissions.filter((p): p is string => typeof p === "string");
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const API_BASE = "/api/v1";

const AUTH_TIMEOUT_MS = 5_000;

async function fetchCurrentUser(accessToken: string): Promise<UserResponse> {
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error("Failed to fetch current user");
  return res.json();
}

async function apiRefreshToken(refreshToken: string | null): Promise<{
  access_token: string;
  refresh_token: string;
  expires_in: number;
}> {
  // Cookie path: refresh token rides on the httpOnly cookie. Body is empty.
  // `credentials: "include"` is required so the browser attaches the cookie.
  if (USE_COOKIE_REFRESH) {
    const res = await fetch(`${API_BASE}/auth/refresh-cookie`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
    });
    if (!res.ok) throw new Error("Token refresh failed");
    return res.json();
  }

  // Legacy path: refresh token comes from localStorage and is sent in the body.
  if (!refreshToken) throw new Error("No refresh token available");
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
    signal: AbortSignal.timeout(AUTH_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error("Token refresh failed");
  return res.json();
}

async function apiLogout(
  accessToken: string,
  refreshToken: string | null
): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// How many ms before expiry to trigger a proactive refresh
const REFRESH_MARGIN_MS = 60_000; // 1 minute

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    permissions: [],
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---------------------------------------------------------------------------
  // scheduleRefresh — set a timer to proactively refresh before expiry
  // ---------------------------------------------------------------------------
  const scheduleRefresh = useCallback((expiresAt: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    const delay = expiresAt - Date.now() - REFRESH_MARGIN_MS;
    if (delay <= 0) return;

    refreshTimerRef.current = setTimeout(() => {
      // We'll trigger refreshToken; if it fails the 401 interceptor logs out
      void refreshTokenFn();
    }, delay);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // refreshToken
  // ---------------------------------------------------------------------------
  const refreshTokenFn = useCallback(async (): Promise<boolean> => {
    const rt = getRefreshToken();
    // In cookie-refresh mode the refresh token lives only in the httpOnly
    // cookie, so a missing localStorage value is expected and not a failure.
    if (!USE_COOKIE_REFRESH && !rt) return false;

    try {
      const data = await apiRefreshToken(rt);
      storeTokens(data.access_token, data.refresh_token, data.expires_in);
      scheduleRefresh(getTokenExpiry());

      // Update user data with fresh token
      const user = await fetchCurrentUser(data.access_token);
      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
        permissions: permissionsFromToken(data.access_token),
      });
      return true;
    } catch {
      clearTokens();
      setState({ user: null, isLoading: false, isAuthenticated: false, permissions: [] });
      return false;
    }
  }, [scheduleRefresh]);

  // ---------------------------------------------------------------------------
  // Bootstrap — check stored token on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function bootstrap() {
      const accessToken = getAccessToken();

      if (!accessToken) {
        setState({ user: null, isLoading: false, isAuthenticated: false, permissions: [] });
        return;
      }

      const expiry = getTokenExpiry();
      const isExpiredOrNearExpiry = expiry - Date.now() < REFRESH_MARGIN_MS;

      if (isExpiredOrNearExpiry) {
        // Try to refresh silently
        const ok = await refreshTokenFn();
        if (!ok) {
          setState({ user: null, isLoading: false, isAuthenticated: false, permissions: [] });
        }
        return;
      }

      // Token looks valid — hydrate user
      try {
        const user = await fetchCurrentUser(accessToken);
        setState({
          user,
          isLoading: false,
          isAuthenticated: true,
          permissions: permissionsFromToken(accessToken),
        });
        scheduleRefresh(expiry);
      } catch {
        // Token rejected by server — attempt refresh
        const ok = await refreshTokenFn();
        if (!ok) {
          setState({ user: null, isLoading: false, isAuthenticated: false, permissions: [] });
        }
      }
    }

    void bootstrap();

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 401 handling is done exclusively by the axios interceptor in services/api.ts.
  // No fetch monkey-patching needed — all API calls go through the axios instance.

  // ---------------------------------------------------------------------------
  // setSession — called after OAuth callback to hydrate auth state
  // ---------------------------------------------------------------------------
  const setSession = useCallback(async (access: string, refresh: string, expiresIn: number) => {
    storeTokens(access, refresh, expiresIn);
    const user = await fetchCurrentUser(access);
    setState({
      user,
      isLoading: false,
      isAuthenticated: true,
      permissions: permissionsFromToken(access),
    });
    scheduleRefresh(Date.now() + expiresIn * 1000);
  }, [scheduleRefresh]);

  // ---------------------------------------------------------------------------
  // login — redirect to Google OAuth
  // ---------------------------------------------------------------------------
  const login = useCallback(async (redirectUri?: string) => {
    const uri = redirectUri ?? `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({ redirect_uri: uri });
    try {
      const res = await fetch(`${API_BASE}/auth/google/url?${params.toString()}`);
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      // Fallback: navigate directly (will show JSON but at least doesn't silently fail)
      window.location.href = `${API_BASE}/auth/google/url?${params.toString()}`;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // switchRole
  // ---------------------------------------------------------------------------
  const switchRole = useCallback(async (targetRole: string | null) => {
    const at = getAccessToken();
    if (!at) return;

    const res = await fetch(`${API_BASE}/auth/switch-role`, {
      method: "POST",
      headers: { Authorization: `Bearer ${at}`, "Content-Type": "application/json" },
      body: JSON.stringify({ target_role: targetRole }),
    });
    if (!res.ok) throw new Error("Failed to switch role");

    const data = await res.json() as { access_token: string; refresh_token: string; expires_in: number };
    storeTokens(data.access_token, data.refresh_token, data.expires_in);
    scheduleRefresh(Date.now() + data.expires_in * 1000);

    const user = await fetchCurrentUser(data.access_token);
    setState({
      user,
      isLoading: false,
      isAuthenticated: true,
      permissions: permissionsFromToken(data.access_token),
    });
  }, [scheduleRefresh]);

  // ---------------------------------------------------------------------------
  // logout
  // ---------------------------------------------------------------------------
  const logout = useCallback(async () => {
    const at = getAccessToken();
    const rt = getRefreshToken();

    if (at) {
      try {
        await apiLogout(at, rt);
      } catch {
        // Best-effort — clear locally regardless
      }
    }

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    clearTokens();
    setState({ user: null, isLoading: false, isAuthenticated: false, permissions: [] });
  }, []);

  // ---------------------------------------------------------------------------
  // Permission helpers
  // ---------------------------------------------------------------------------
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!state.user) return false;
      // Bypass on the *current* session's superadmin status (currently_superadmin),
      // not the DB identity (is_superadmin). When a superadmin switches to a
      // lower role the backend stops bypassing, so the UI must mirror that to
      // avoid showing controls that will 403.
      if (state.user.currently_superadmin) return true;
      // Permissions are decoded from the JWT access token at login/refresh time.
      // Backend remains the source of truth — this is for client-side UI gating only.
      return state.permissions.includes(permission);
    },
    [state.user, state.permissions]
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (!state.user) return false;
      if (state.user.currently_superadmin) return true;
      if (permissions.length === 0) return true;
      const userSet = new Set(state.permissions);
      return permissions.some((p) => userSet.has(p));
    },
    [state.user, state.permissions]
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      if (!state.user) return false;
      // Check against effective_roles (the JWT) so a downscoped superadmin
      // doesn't appear to hold roles the backend has stripped from their
      // session. Falls back to roles[] for clients that haven't refreshed
      // /auth/me since the schema added effective_roles.
      const source = state.user.effective_roles?.length
        ? state.user.effective_roles
        : state.user.roles;
      return source.includes(role);
    },
    [state.user]
  );

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshToken: refreshTokenFn,
    setSession,
    switchRole,
    hasPermission,
    hasAnyPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

// Re-export for convenience
export { getAccessToken as getAccessToken };
